import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CalendarEvent } from "../../hooks/useCalendarEvents";

const ENTITY_TYPES = ["Individual", "SoleTrader", "Company", "Family", "Trust"];

const STEPS = ["Contact", "Client", "Import"];

export interface OnboardClientDialogProps {
  open: boolean;
  event: CalendarEvent | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (
    event: CalendarEvent,
    details: { contactName: string; contactEmail: string; clientName: string; entityType: string },
  ) => void;
}

/**
 * The yellow row's wizard: the meeting is a 1-1 with someone who isn't a Prospo
 * client, so the contact and client have to exist before the meeting can be
 * imported. Prefilled from the calendar invite — usually the advisor only has to
 * confirm and press Add.
 */
const OnboardClientDialog: React.FC<OnboardClientDialogProps> = ({
  open,
  event,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [step, setStep] = useState(0);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [entityType, setEntityType] = useState("Individual");

  // Seed from the invite when a different event opens the dialog. Deliberately
  // keyed on the counterparty rather than the event object — the parent
  // re-renders a new object identity on every calendar tick, and depending on
  // that would wipe whatever the advisor had typed.
  const seedName = event?.counterparty?.name || "";
  const seedEmail = event?.counterparty?.email || "";

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setContactName(seedName);
    setContactEmail(seedEmail);
    // The client is usually the person themselves, so default to their name and
    // let the advisor change it for a trust or company.
    setClientName(seedName);
    setEntityType("Individual");
  }, [open, seedName, seedEmail]);

  const when = event
    ? `${dayjs(event.start).format("ddd D MMM YYYY")} · ${dayjs(event.start).format("hh:mm A")}`
    : "";

  const stepValid = useMemo(() => {
    if (step === 0) return contactEmail.trim().length > 0 && contactName.trim().length > 0;
    if (step === 1) return clientName.trim().length > 0;
    return true;
  }, [step, contactName, contactEmail, clientName]);

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else if (event) {
      onConfirm(event, {
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        clientName: clientName.trim(),
        entityType,
      });
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Add to Prospo</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.95rem" }}>
            {event?.title}
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: "0.85rem" }}>{when}</Typography>
        </Box>

        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ color: "#6b7280", fontSize: "0.82rem" }}>
              This person isn&apos;t in Prospo yet. They&apos;ll be created as a contact.
            </Typography>
            <TextField
              label="Contact name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              disabled={submitting}
              autoFocus
              fullWidth
            />
            <TextField
              label="Contact email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              disabled={submitting}
              fullWidth
            />
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ color: "#6b7280", fontSize: "0.82rem" }}>
              The client this meeting belongs to. Change the name for a trust or company.
            </Typography>
            <TextField
              label="Client name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              disabled={submitting}
              autoFocus
              fullWidth
            />
            <TextField
              select
              label="Entity type"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              disabled={submitting}
              fullWidth
            >
              {ENTITY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ color: "#6b7280", fontSize: "0.82rem", mb: 1 }}>
              Adding will create the following and import the meeting:
            </Typography>
            <Typography sx={{ fontSize: "0.85rem" }}>
              <strong>Contact:</strong> {contactName} · {contactEmail}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem" }}>
              <strong>Client:</strong> {clientName} ({entityType})
            </Typography>
            <Typography sx={{ fontSize: "0.85rem" }}>
              <strong>Meeting:</strong> {event?.title}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={step === 0 ? onCancel : () => setStep((s) => s - 1)}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        <Button
          variant="contained"
          disabled={!stepValid || submitting || !event}
          onClick={handleNext}
          sx={{ textTransform: "none" }}
        >
          {submitting ? "Adding…" : step === STEPS.length - 1 ? "Add" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardClientDialog;
